import axios from 'axios';
import { toast } from 'react-toastify';
import { disconnectWebSocket } from '../config/WebsocketService';

class AuthService {
    constructor() {
        this.sessionData = null;
        this.sessionPromise = null;
    }

    async login(identificationNumber, username, password) {
        try {
            const response = await axios.post(process.env.REACT_APP_API_LOGIN_ROUTE, {
                identificationNumber,
                username,
                password
            }, {
                withCredentials: true
            });

            if (response.data.success) {
                await this.fetchSession();
                sessionStorage.setItem('showNotificationPopups', '1');
                window.dispatchEvent(new Event('loginSuccess'));
            }

            return response.data;
        } catch (error) {
            this.handleAuthError(error);
            throw error;
        }
    }

    async fetchSession() {
        const meRoute = process.env.REACT_APP_API_USER_ME_ROUTE;

        if (!meRoute || meRoute.includes('${')) {
            console.error('REACT_APP_API_USER_ME_ROUTE no está configurada correctamente:', meRoute);
            this.sessionData = null;
            return null;
        }

        try {
            const response = await axios.get(meRoute, {
                withCredentials: true
            });
            
            if (response.data.authenticated) {
                this.sessionData = response.data;
                return this.sessionData;
            } else {
                this.sessionData = null;
                return null;
            }
        } catch (error) {
            this.sessionData = null;
            
            if (error.response?.status === 401 || error.response?.status === 403) {
                return null;
            }

            // Sin respuesta del servidor (backend caído, CORS, red)
            if (!error.response) {
                console.warn('No se pudo verificar la sesión:', error.message);
                return null;
            }
            
            throw error;
        }
    }

    async validateSession() {
        if (!this.sessionPromise) {
            this.sessionPromise = this.fetchSession().finally(() => {
                this.sessionPromise = null;
            });
        }
        return this.sessionPromise;
    }

    getSessionData() {
        return this.sessionData;
    }

    async changePassword(currentPassword, newPassword, confirmNewPassword) {
        try {
            const response = await axios.put(
                process.env.REACT_APP_API_USER_CHANGE_PASSWORD_ROUTE,
                {
                    currentPassword,
                    newPassword,
                    confirmNewPassword
                },
                { withCredentials: true }
            );
            return response.data;
        } catch (error) {
            throw error;
        }
    }

    async logout() {
        try {
            await axios.get(process.env.REACT_APP_API_LOGOUT_ROUTE, {
                withCredentials: true
            });
        } catch (error) {
            console.error('Error during logout:', error);
        } finally {
            disconnectWebSocket({ allowReconnect: false });
            this.clearAuthData();
        }
    }

    clearAuthData() {
        this.sessionData = null;
        this.sessionPromise = null;
        
        localStorage.removeItem('authToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('tokenExpiration');
    }

    handleAuthError(error) {
        if (error.response) {
            switch (error.response.status) {
                case 401:
                    toast.error('Credenciales inválidas');
                    break;
                case 403:
                    toast.error('No tiene permisos para realizar esta acción');
                    break;
                case 429:
                    toast.error('Demasiados intentos. Por favor, espere un momento');
                    break;
                default:
                    toast.error('Error de autenticación');
            }
        } else if (error.request) {
            toast.error('Error de conexión. Por favor, verifique su conexión a internet');
        } else {
            toast.error('Error inesperado');
        }
    }

    async isAuthenticated() {
        if (this.sessionData && this.sessionData.authenticated) {
            return true;
        }
        
        const session = await this.validateSession();
        return session && session.authenticated;
    }
}

export const authService = new AuthService();
